import React from "react";
import dynamic from "next/dynamic";
import Link from "next/link";

import { useEventTracker } from "@/hooks/useEventTracker";
import { useSsoUrls } from "@/hooks/useSsoUrls";
import useUserFavorites from "@/hooks/useUserFavorites";
import { useSelector } from "@/redux/hooks";
import { getIsLoggedIn } from "@/redux/selectors/user";

import { StrainCardPlaceholder } from "@/components/botanic/StrainCard";
import HeartOutlineIcon from "@/components/Icons/heart_outline.svg";
import Placeholder, { Rect } from "@/components/Skeleton";
import TrackImpression from "@/components/TrackImpression";

const StrainCard = dynamic(() => import("@/components/botanic/StrainCard"));

const FavoriteStrainsSection = () => {
  const { signInUrl, signUpUrl } = useSsoUrls();
  const { publishEvent } = useEventTracker();

  const trackClickEvent = (label: string) =>
    publishEvent({
      action: "Click",
      category: "Strain List Favorites",
      label,
    });

  const { favoritedStrains, favoritesEnabled } = useUserFavorites();
  const userIsLoggedIn = useSelector(getIsLoggedIn);

  const favoriteStrains = favoritedStrains?.strains?.slice(0, 3);
  const totalFavoriteStrains = favoritedStrains?.totalCount || 0;

  return (
    <div className="py-xxl border-t border-light-grey">
      {favoritesEnabled ? (
        favoriteStrains?.length ? (
          <TrackImpression
            category="Strain List Favorites"
            label="Favorite Strains"
          >
            <h2 className="text-md mb-md">Your favorite weed strains</h2>
            <div className="row">
              {favoriteStrains.map((strain) => (
                <div className="md:col-1/3 mb-md md:mb-none" key={strain.id}>
                  <StrainCard
                    strain={strain}
                    trackingCategory="Strain List Favorites"
                    trackingLabel="Strain Card"
                    orientation="horizontal"
                    preserveHeight={true}
                  />
                </div>
              ))}
            </div>
            {totalFavoriteStrains > 3 && (
              <Link
                href="/me/favorites"
                onClick={() => {
                  trackClickEvent("See all favorites");
                }}
                className="block mt-md md:mt-xl underline"
              >
                see all favorites
              </Link>
            )}
          </TrackImpression>
        ) : (
          <div
            className="md:flex md:flex-col md:items-center md:text-center md:mx-auto"
            style={{ maxWidth: 350 }}
          >
            <span className="inline-block shadow-low rounded-full bg-white text-green p-xs mb-xl">
              <div className="flex flex-row items-center">
                <span className="px-sm">
                  <HeartOutlineIcon height="24" width="24" />
                </span>
                <span className="pl-xs pr-sm text-grey text-xs font-bold content-center">
                  0
                </span>
              </div>
            </span>
            <h2 className="text-md">Save your faves</h2>
            {userIsLoggedIn ? (
              <TrackImpression
                category="Strain List Favorites"
                label="Favorite Strains Empty State - Logged in"
              >
                <p className="mb-xl">
                  No favorites yet! Use the heart to save your favorite strains
                  to your account.
                </p>
                <Link
                  href="/strains"
                  className="button mx-auto button--secondary"
                  style={{ maxWidth: 322 }}
                  onClick={() => {
                    trackClickEvent("Empty state see all");
                  }}
                >
                  see all strains
                </Link>
              </TrackImpression>
            ) : (
              <TrackImpression
                category="Strain List Favorites"
                label="Favorite Strains Empty State - Logged out"
              >
                <p className="mb-xl">
                  Log in or create an account to keep track of your favorite
                  strains.
                </p>
                {signUpUrl && (
                  <div className="w-full mx-auto" style={{ maxWidth: 322 }}>
                    <a
                      className="button button--secondary"
                      href={signUpUrl}
                      onClick={() => {
                        trackClickEvent("Empty state sign up");
                      }}
                    >
                      create an account
                    </a>
                    <a
                      className="underline mx-auto block text-center mt-lg"
                      href={signInUrl}
                      onClick={() => {
                        trackClickEvent("Empty state sign in");
                      }}
                    >
                      log in
                    </a>
                  </div>
                )}
              </TrackImpression>
            )}
          </div>
        )
      ) : (
        <div>
          <Placeholder width="60%" height="30" className="mb-md">
            <Rect width="100%" height="100%" />
          </Placeholder>
          <div className="row">
            <div className="md:col-1/3">
              <StrainCardPlaceholder
                orientation="horizontal"
                className="h-full shadow-low rounded"
              />
            </div>
            <div className="md:col-1/3">
              <StrainCardPlaceholder
                orientation="horizontal"
                className="h-full shadow-low rounded"
              />
            </div>
            <div className="md:col-1/3">
              <StrainCardPlaceholder
                orientation="horizontal"
                className="h-full shadow-low rounded"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FavoriteStrainsSection;
